import { useEffect, useState } from 'react';

import { isRunningInNativeApp, native } from '@muzly/shared';
import type { NetworkStatus } from '@muzly/shared';

const BROWSER_FALLBACK_STATUS: NetworkStatus = {
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  connectionType: 'unknown',
};

// ============================================================
// useNetworkStatus
// ============================================================
//
// Returns the current connectivity state. Inside the native app
// it sources data from the bridge (`getNetworkStatus` + the
// `networkStatusChanged` push notification). On the open web it
// falls back to `navigator.onLine` + window `online`/`offline`
// events (which is less detailed but enough for an offline banner).
// ============================================================

export const useNetworkStatus = (): NetworkStatus => {
  const [status, setStatus] = useState<NetworkStatus>(BROWSER_FALLBACK_STATUS);

  useEffect(() => {
    if (isRunningInNativeApp()) {
      let cancelled = false;
      native
        .invoke('getNetworkStatus')
        .then((initial) => {
          if (!cancelled) {
            setStatus(initial);
          }
        })
        .catch(() => {
          // ignore — keep fallback status
        });
      const off = native.on('networkStatusChanged', (next) => {
        setStatus(next);
      });
      return () => {
        cancelled = true;
        off();
      };
    }

    const update = () => {
      setStatus({
        online: navigator.onLine,
        connectionType: 'unknown',
      });
    };
    update();
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return status;
};
